import React, { useEffect, useState, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { useDispatch } from 'react-redux';
import { fetchMainFocusDistribution } from './store';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList
} from 'recharts';
import { FaHeartbeat } from 'react-icons/fa';

const COLORS = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#f59e0b', // Orange
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#3bcaf6', // Cyan
    '#6366f1', // Indigo
    '#f43f5e', // Rose
    '#84cc16'  // Lime
];

const formatLabel = (label) => {
    if (!label) return '';
    // CamelCase to Space Case
    return label
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                padding: '12px 16px',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                border: '1px solid #f1f5f9',
                minWidth: '160px'
            }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 800, color: '#1e293b', fontSize: '14px', borderBottom: '1px solid #f8fafc', paddingBottom: '6px' }}>
                    {formatLabel(data.name)}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Male</span>
                        <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '13px' }}>: {data.male || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Female</span>
                        <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '13px' }}>: {data.female || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>Others</span>
                        <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '13px' }}>: {data.others || 0}</span>
                    </div>
                    <div style={{ marginTop: '6px', paddingTop: '8px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#4f46e5', fontSize: '13px', fontWeight: 600 }}>Total Count</span>
                        <span style={{ color: '#4f46e5', fontWeight: 800, fontSize: '13px' }}>: {data.count || 0}</span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

const MainFocusAreas = () => {
    const dispatch = useDispatch();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalResponses, setTotalResponses] = useState(0);
    const dataFetchedRef = useRef(false);

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;

        const cacheKey = 'MAIN_FOCUS_DISTRIBUTION_CACHE';
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') return;

            const cacheTimestamp = data.cacheTimestamp || 0;
            const threeMinutes = 3 * 60 * 1000;
            const isExpired = Date.now() - cacheTimestamp > threeMinutes;
            if (!isExpired) {
                setData(data.data);
                setTotalResponses(data.totalResponses);
                setLoading(false);
                return;
            } else {
                sessionStorage.removeItem(cacheKey);
            }
        }

        // Mark as pending
        sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

        dispatch(fetchMainFocusDistribution())
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const fetchedData = response.payload.data;
                    setData(fetchedData);
                    // Calculate total
                    const total = fetchedData.reduce((acc, curr) => acc + curr.count, 0);
                    setTotalResponses(total);
                    sessionStorage.setItem(cacheKey, JSON.stringify({ data: fetchedData, totalResponses: total, cacheTimestamp: Date.now() }));
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching main focus distribution:", error);
                setLoading(false);
            });
    }, [dispatch]);

    if (loading) {
        return (
            <div style={{
                background: '#fff',
                borderRadius: '24px',
                padding: '32px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                height: '450px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <ComponentLoader color="#4f46e5" size="40px" />
            </div>
        );
    }

    return (
        <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px 10px 10px 15px', // Reduced left and bottom padding
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9',
            position: 'relative',
            height: '450px',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', paddingRight: '15px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0px' }}>
                        <FaHeartbeat style={{ color: '#4f46e5', fontSize: '18px' }} />
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>User's Main Focus</h3>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Responses</div>
                    <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', lineHeight: '1' }}>{totalResponses}</div>
                </div>
            </div>

            <div style={{
                flex: 1,
                width: '100%',
                overflowY: 'auto',
                paddingRight: '5px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#e2e8f0 transparent'
            }}>
                <div style={{ height: Math.max(380, data.length * 60) + 'px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            layout="vertical"
                            data={data}
                            margin={{ top: 10, right: 60, left: 0, bottom: 5 }}
                            barSize={30}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                            <XAxis type="number" hide />
                            <YAxis
                                dataKey="name"
                                type="category"
                                axisLine={false}
                                tickLine={false}
                                width={110}
                                tick={{ fontSize: 13, fontWeight: 700, fill: '#475569' }}
                                tickFormatter={formatLabel}
                            />
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{ fill: '#f8fafc' }}
                            />
                            <Bar
                                dataKey="count"
                                radius={[0, 6, 6, 0]}
                                animationDuration={1500}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                                <LabelList
                                    dataKey="count"
                                    position="right"
                                    style={{ fill: '#475569', fontSize: '13px', fontWeight: 800 }}
                                    offset={12}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Subtle decorative elements for premium feel */}
            <div style={{
                position: 'absolute',
                right: '12px',
                top: '100px',
                bottom: '24px',
                width: '4px',
                background: '#f1f5f9',
                borderRadius: '2px',
                opacity: 0.5
            }}></div>
        </div>
    );
};

export default MainFocusAreas;
